import axios from 'axios';

import { LanguageTag, SupportedLocale } from '@flipster/i18n/types';
import { CookieKeys, cookieStore } from '@flipster/utils/cookie';
import { getServerHostname, isServer } from '@flipster/utils/env-helper';
import { getPrexInstallationId } from '@flipster/utils/localStorage';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    /**
     * 요청을 보낸 시점의 nuts 쿠키 스냅샷.
     * 응답이 401로 돌아왔을 때 `sessionErrorHandler`에서 현재 nuts와 비교하여,
     * 다른 계정으로 세션이 이미 교체된 후에 돌아온 stale 응답이면 logout을 건너뛰기 위한 값.
     */
    nutsAtSend?: string;
  }
  interface AxiosRequestConfig {
    nutsAtSend?: string;
  }
}

const baseURL = `${getServerHostname()}/api`;
const timeout = 10 * 1000;

export const getAcceptLanguage = (locale: string): LanguageTag => {
  switch (locale) {
    case 'zh-CN':
      return 'zh-CN';
    case 'zh-TW':
      return 'zh-TW';
    case 'ru':
      return 'ru-RU';
    case 'vi':
      return 'vi-VN';
    case 'ja':
      return 'ja-JP';
    case 'en-AE':
      return 'en-AE';
    default:
      return 'en-US';
  }
};

// HTML lang 기반으로 Accept-Language 헤더 설정
const getCurrentAcceptLanguage = (): LanguageTag => {
  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement.lang as SupportedLocale;
    if (['en', 'vi', 'zh-CN', 'zh-TW', 'ru', 'ja', 'en-AE'].includes(htmlLang)) {
      return getAcceptLanguage(htmlLang);
    }
  }
  return getAcceptLanguage('en');
};

const apiClient = axios.create({
  baseURL,
  timeout,
  withCredentials: true,
  headers: {
    'X-Prex-Client-Platform': 'web',
    'X-Prex-Client-Version': process.env.NEXT_PUBLIC_VERSION ?? '',
  },
});

// CORS Preflight 요청으로 인한 지연을 방지하기 위해 일부 API에 대해서는
// 몇가지 제약(Datadog 추적이 안되는 등)을 감수하고 Simple Request를 사용하기로 함
// https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#simple_requests
const simpleApiClient = axios.create({ baseURL, timeout });

[apiClient, simpleApiClient].forEach((client) => {
  client.interceptors.request.use((config) => {
    if (isServer) throw new Error('Cannot use axios on server side');

    // 매번 현재 HTML lang 기반으로 Accept-Language 헤더 설정
    config.headers['Accept-Language'] = getCurrentAcceptLanguage();

    // 요청 시점의 nuts 스냅샷. `sessionErrorHandler`에서 stale 응답 판별에 사용.
    config.nutsAtSend = cookieStore.get(CookieKeys.Nuts);

    return config;
  });
});

// prex installation id를 헤더로 전달. 첫 요청 시점에 없으면 생성한다.
// 비-safelisted 커스텀 헤더라 CORS preflight를 유발하므로, Simple Request 최적화를 위해
// 만든 simpleApiClient에는 붙이지 않고 apiClient에만 적용한다.
apiClient.interceptors.request.use((config) => {
  config.headers['X-Prex-Installation-Id'] = getPrexInstallationId();
  return config;
});

export { apiClient, simpleApiClient };
